import { TNullable } from '@/types';
import {
  TInstitutionServerCamel,
  TInstitutionWhiteLabelServerCamel,
} from '@/types/institutions';
import { LOCAL_STORAGE_KEYS, SESSION_STORAGE_KEYS } from '@/utils/constants';
import { navigateToLogin } from '@/utils/navigateToLogin';

/**
 * Returns the subdomain an institution should be accessed on based on its whitelabel settings.
 * Whitelabel-enabled institutions use their configured subdomain;
 * all others fall back to the default app subdomain.
 * @param whitelabel - The whitelabel settings to resolve.
 * @returns Lowercase subdomain string.
 */
export const getInstitutionWhitelabelSubdomain = (
  whitelabel: TNullable<Partial<TInstitutionWhiteLabelServerCamel>>,
): string => {
  return whitelabel?.enabled && whitelabel?.subdomain
    ? whitelabel.subdomain.toLowerCase()
    : import.meta.env.VITE_DEFAULT_SUBDOMAIN;
};

type TRedirectToInstitutionParams = {
  currentInstitution: TInstitutionServerCamel;
  targetInstitution: TInstitutionServerCamel;
  redirect?: string;
};

/**
 * Triggers a full page reload to switch the current institution. All institution
 * switches (same or cross-subdomain) perform a redirect to the login page with
 * the target institution's ID and current authentication token.
 * @param params - Redirect options.
 * @param params.currentInstitution - The institution currently active.
 * @param params.targetInstitution - The institution to switch to.
 * @param params.redirect - Optional path to return to after re-authenticating.
 */
export const redirectToInstitution = ({
  currentInstitution,
  targetInstitution,
  redirect,
}: TRedirectToInstitutionParams) => {
  if (redirect) {
    sessionStorage.setItem(SESSION_STORAGE_KEYS.REDIRECT_PATH, redirect);
  }

  const currentSubdomain = getInstitutionWhitelabelSubdomain(
    currentInstitution.whitelabel,
  );
  const targetSubdomain = getInstitutionWhitelabelSubdomain(
    targetInstitution.whitelabel,
  );

  // If subdomains differ, keep the current institution ID in this subdomain's localStorage.
  // This prevents unwanted redirects when this subdomain is visited again.
  localStorage.setItem(
    LOCAL_STORAGE_KEYS.CURRENT_INSTITUTION_ID,
    targetSubdomain === currentSubdomain
      ? targetInstitution.id
      : currentInstitution.id,
  );

  navigateToLogin({
    subdomain: targetSubdomain,
    attachToken: true,
    institutionId: targetInstitution.id,
  });
};
